// owner/screens/Settings.js — «الإعدادات» (/settings). No mock-up of its own; built in the approved owner kit
// (tasks/owner-mobile-app-build.md §10 "8c batch 3", approved by Adil 4 Nov 2026):
//   - profile: view-only (decision 5) — changes go through team@hostme.sa;
//   - password: current, new, confirm; the minimum is the SERVER's rule (utils/passwordPolicy.js), so this screen
//     only checks the two new entries match; errors come from codes, in the reader's language. On success the
//     server has ended every session, this one included, so the owner is signed out;
//   - alerts: the owner's switches (new booking, cancellation), then «هذا الجهاز» — this device's alerts and a test
//     (owner/alerts/ThisDeviceAlerts.js, 8d): a browser on a computer, the app on a phone;
//   - account closure and data requests by email (§2 decision 11).

import React, { useEffect, useRef, useState } from 'react';
import useLanguage from '../../hooks/useLanguage';
import { typeLabel } from '../../config/notificationLabels';
import Icon from '../ui/Icon';
import { InlineError } from '../ui/States';
import useOwnerSignOut from '../shell/useOwnerSignOut';
import { useOwnerProfile, useAlertSettings } from '../api/ownerReads';
import { useSetAlertPreference, useChangePassword } from '../api/ownerWrites';
import ThisDeviceAlerts from '../alerts/ThisDeviceAlerts';

const TEAM_EMAIL = 'team@hostme.sa';
const SIGN_OUT_AFTER_MS = 3000;

// Server codes → this screen's words (I18N_STRATEGY: translate from the code, never from the message).
const PASSWORD_ERRORS = { VAL_004: 'tooShort', AUTH_001: 'wrongCurrent', AUTH_013: 'demo' };

const Mail = () => <a className="o-ltr o-mail" href={`mailto:${TEAM_EMAIL}`}>{TEAM_EMAIL}</a>;

/** A sentence with the team's email in place of {email}. */
const WithEmail = ({ text }) => {
  const [before, after] = text.split('{email}');
  return <>{before}<Mail />{after}</>;
};

const Profile = () => {
  const { t } = useLanguage();
  const me = useOwnerProfile();
  return (
    <section className="o-section">
      <div className="o-section-head"><h2>{t('ownerApp.settings.profile')}</h2></div>
      {me.isLoading && <div className="o-sk" style={{ height: 150, borderRadius: 20 }} aria-busy="true" />}
      {me.isError && <div className="o-sheet"><InlineError text={t('ownerApp.settings.profileError')} onRetry={me.refetch} /></div>}
      {me.data && (
        <ul className="o-sheet o-kv">
          <li><span className="o-k">{t('ownerApp.settings.name')}</span><span className="o-v">{me.data.name}</span></li>
          <li><span className="o-k">{t('ownerApp.settings.email')}</span><span className="o-v o-ltr">{me.data.email}</span></li>
          {me.data.phone && (
            <li><span className="o-k">{t('ownerApp.settings.phone')}</span><span className="o-v o-ltr">{me.data.phone}</span></li>
          )}
        </ul>
      )}
      <p className="o-footnote"><WithEmail text={t('ownerApp.settings.profileChange')} /></p>
    </section>
  );
};

const EMPTY_FORM = { currentPassword: '', newPassword: '', confirmPassword: '' };

const Password = () => {
  const { t } = useLanguage();
  const signOut = useRef(null);
  signOut.current = useOwnerSignOut();
  const change = useChangePassword();
  const [form, setForm] = useState(EMPTY_FORM);
  const [problem, setProblem] = useState('');

  // Signed out a moment after a change: every session has already ended on the server.
  useEffect(() => {
    if (!change.isSuccess) return undefined;
    // The server has already ended every session, so the sign-out makes no request on the dead pass.
    const timer = setTimeout(() => signOut.current({ sessionEnded: true }), SIGN_OUT_AFTER_MS);
    return () => clearTimeout(timer);
  }, [change.isSuccess]);

  const set = (field) => (e) => { setForm({ ...form, [field]: e.target.value }); setProblem(''); };

  const submit = (e) => {
    e.preventDefault();
    if (!form.currentPassword || !form.newPassword || !form.confirmPassword) return setProblem('missing');
    if (form.newPassword !== form.confirmPassword) return setProblem('mismatch');
    setProblem('');
    return change.mutate(
      { currentPassword: form.currentPassword, newPassword: form.newPassword },
      { onError: (error) => setProblem(PASSWORD_ERRORS[error?.response?.data?.code] || (error?.response ? 'generic' : 'network')) }
    );
  };

  const field = (name, autoComplete) => (
    <div className="o-field">
      <label htmlFor={`pw-${name}`}>{t(`ownerApp.settings.${name}`)}</label>
      <input id={`pw-${name}`} type="password" dir="ltr" autoComplete={autoComplete} value={form[name]}
        onChange={set(name)} disabled={change.isPending || change.isSuccess} />
    </div>
  );

  return (
    <section className="o-section">
      <div className="o-section-head"><h2>{t('ownerApp.settings.password')}</h2></div>
      <form className="o-sheet o-form" onSubmit={submit} noValidate>
        {field('currentPassword', 'current-password')}
        {field('newPassword', 'new-password')}
        <p className="o-hint">{t('ownerApp.settings.passwordHint')}</p>
        {field('confirmPassword', 'new-password')}
        {problem && <p className="o-form-error" role="alert">{t(`ownerApp.settings.passwordErrors.${problem}`)}</p>}
        {change.isSuccess && <p className="o-form-ok" role="status"><Icon name="check" size="sm" />{t('ownerApp.settings.passwordChanged')}</p>}
        <button type="submit" className="o-btn o-btn-primary o-btn-block" disabled={change.isPending || change.isSuccess}>
          {change.isPending ? t('ownerApp.settings.changing') : t('ownerApp.settings.changePassword')}
        </button>
      </form>
    </section>
  );
};

const AlertSwitch = ({ type, on, onToggle, busy }) => {
  const { t } = useLanguage();
  const { label, description } = typeLabel(t, type);
  return (
    <li className="o-switch-row">
      <div className="o-body">
        <p className="o-t" id={`alert-${type}`}>{label}</p>
        <p className="o-m">{description}</p>
      </div>
      <button type="button" role="switch" aria-checked={on} aria-labelledby={`alert-${type}`}
        className="o-switch" onClick={() => onToggle(type, !on)} disabled={busy}>
        <span className="o-knob" />
      </button>
    </li>
  );
};

const Alerts = () => {
  const { t } = useLanguage();
  const settings = useAlertSettings();
  const setPreference = useSetAlertPreference();
  const unavailable = settings.isError && settings.error?.response?.status === 403;
  const types = (settings.data?.types || []).map((x) => x.key);
  return (
    <section className="o-section">
      <div className="o-section-head"><h2>{t('ownerApp.settings.alerts')}</h2></div>
      {settings.isLoading && <div className="o-sk" style={{ height: 120, borderRadius: 20 }} aria-busy="true" />}
      {unavailable && <div className="o-sheet"><p className="o-empty-row">{t('ownerApp.settings.alertsUnavailable')}</p></div>}
      {settings.isError && !unavailable && (
        <div className="o-sheet"><InlineError text={t('ownerApp.settings.alertsError')} onRetry={settings.refetch} /></div>
      )}
      {settings.data && (
        <ul className="o-sheet o-rows">
          {types.map((type) => (
            <AlertSwitch key={type} type={type} on={settings.data.preferences?.[type] !== false}
              busy={setPreference.isPending} onToggle={(key, enabled) => setPreference.mutate({ type: key, enabled })} />
          ))}
        </ul>
      )}
      {setPreference.isError && (
        <p className="o-form-error" role="alert">
          {t(setPreference.error?.response?.data?.code === 'AUTH_013'
            ? 'ownerApp.settings.passwordErrors.demo' : 'ownerApp.settings.alertsSaveError')}
        </p>
      )}
      {settings.data && <ThisDeviceAlerts />}
    </section>
  );
};

const Settings = () => {
  const { t } = useLanguage();
  return (
    <>
      <div className="o-page-title">
        <h1>{t('ownerApp.settings.title')}</h1>
        <span className="o-underline" />
      </div>
      <Profile />
      <Password />
      <Alerts />
      <section className="o-section o-section-last">
        <div className="o-section-head"><h2>{t('ownerApp.settings.accountTitle')}</h2></div>
        <div className="o-sheet o-note"><p><WithEmail text={t('ownerApp.settings.accountClosure')} /></p></div>
      </section>
    </>
  );
};

export default Settings;
